import React from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as S from './PackageActivationModal.styles';
import type { PackageActivationSelection } from '@/features/Profile/sections/Packages/hooks/usePackageActivation';
import { CheckIcon, DiscountPercentIcon } from '@/components/UI/Icons/Icons';
import { DateTimeFormat } from '@/constants/common';
import { formatDate, formatNumber } from '@/utils/helpers';
import { InvoiceStartsAt } from '@/api/types/payments.types';
import { usePackageInvoice } from '@/features/Profile/sections/Packages/hooks';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Button } from '@/components/UI/Button/Button';
import { InvoicePaymentModal } from '@/features/Profile/sections/Packages/components/InvoicePaymentModal';
import { getPackagePriceDisplayState } from '@/features/Profile/sections/Packages/PackagesSection.helpers';

type PackageActivationModalProps = PackageActivationSelection & {
  isOpen: boolean;
  onClose: () => void;
};

export function PackageActivationModal({
  activePackageExpiresAt,
  isOpen,
  item,
  onClose,
}: PackageActivationModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    cashBalance,
    closeNullifyWarning,
    closePayment,
    createInvoiceAndPayment,
    paymentPackage,
    handlePay,
    invoicePayment,
    invoiceWarning,
    isActivationContinueDisabled,
    isNullifyWarningOpen,
    isPaymentOpen,
    isProcessingPayment,
    openPayment,
    selectStartsAt,
    startsAt,
  } = usePackageInvoice(item, onClose);

  const { hasCombinedLabels, hasOriginalPrice, hasPromoPriceLabel } =
    getPackagePriceDisplayState(paymentPackage);
  const formattedExpirationDate = activePackageExpiresAt
    ? formatDate(activePackageExpiresAt, DateTimeFormat.date)
    : null;
  const activationDate =
    startsAt === InvoiceStartsAt.MAX_EXPIRES_DATE && formattedExpirationDate
      ? formattedExpirationDate
      : formatDate(new Date().toISOString(), DateTimeFormat.date);
  const {
    name,
    showPromotionLabel,
    benefitPercent,
    originalPrice,
    price,
    periodDays,
    walletAmount,
    paidAdsCount,
    discount,
    promoPrice,
    promotionDiscountAmount,
    promotionDiscountPercent,
    showPromoPriceLabel,
  } = paymentPackage;

  return (
    <>
      <S.Sheet
        background={theme.palette.secondary.background}
        dataTestId="package-activation-modal"
        isInPortal
        isOpen={isOpen && !isPaymentOpen}
        maxWidth={600}
        onClose={onClose}
        title={t('pages.cabinet.package')}
      >
        <S.Content>
          <S.PackageCard>
            <S.PackageHeader>
              <S.NameBox>
                <S.Name>{name}</S.Name>
                {hasCombinedLabels && (
                  <S.CompactBenefitBadge>
                    <DiscountPercentIcon aria-hidden="true" />
                  </S.CompactBenefitBadge>
                )}
              </S.NameBox>
              {hasPromoPriceLabel && !showPromotionLabel && (
                <S.BenefitBadge>
                  {t('pages.cabinet.benefit_percent', {
                    discount: benefitPercent,
                  })}
                </S.BenefitBadge>
              )}
              {showPromotionLabel && (
                <S.AdditionalDiscountBadge>
                  {t('pages.cabinet.additional_discount')}
                </S.AdditionalDiscountBadge>
              )}
            </S.PackageHeader>
            <S.PriceBox $hasOriginalPrice={hasOriginalPrice}>
              {hasOriginalPrice && (
                <S.OriginalPrice>
                  {formatNumber(originalPrice, 0)} ₼
                </S.OriginalPrice>
              )}
              <S.PriceRow>
                <S.Price>{formatNumber(price, 0)} ₼</S.Price>
                <S.Period>
                  {t('pages.cabinet.for_package_period', {
                    days: periodDays,
                  })}
                </S.Period>
              </S.PriceRow>
            </S.PriceBox>
            <S.Features>
              {walletAmount !== null && (
                <S.Feature>
                  <CheckIcon />
                  {t('pages.cabinet.package_wallet_amount', {
                    amount: formatNumber(walletAmount, 0),
                  })}
                </S.Feature>
              )}
              {paidAdsCount !== null && (
                <S.Feature>
                  <CheckIcon />
                  {t('pages.cabinet.package_paid_ads_count', {
                    adsCount: formatNumber(paidAdsCount, 0),
                  })}
                </S.Feature>
              )}
              {Boolean(discount) && (
                <S.Feature>
                  <CheckIcon />
                  {t('pages.cabinet.discount_for_services', { discount })}
                </S.Feature>
              )}
            </S.Features>
          </S.PackageCard>

          <S.ActivationCard>
            <S.ActivationTitle>
              {t('pages.cabinet.activate_package')}
            </S.ActivationTitle>
            <S.DateOptions role="group">
              <S.DateOption
                data-stat="package-day-today"
                $isActive={startsAt === InvoiceStartsAt.TODAY}
                aria-pressed={startsAt === InvoiceStartsAt.TODAY}
                onClick={() => selectStartsAt(InvoiceStartsAt.TODAY)}
              >
                {t('common.today')}
              </S.DateOption>
              {formattedExpirationDate && (
                <S.DateOption
                  data-stat="package-day-expire-date"
                  $isActive={startsAt === InvoiceStartsAt.MAX_EXPIRES_DATE}
                  aria-pressed={startsAt === InvoiceStartsAt.MAX_EXPIRES_DATE}
                  onClick={() =>
                    selectStartsAt(InvoiceStartsAt.MAX_EXPIRES_DATE)
                  }
                >
                  {t('pages.cabinet.date_from', {
                    date: formattedExpirationDate,
                  })}
                </S.DateOption>
              )}
            </S.DateOptions>
          </S.ActivationCard>

          <S.ActionButtonSection>
            <S.ContinueButton
              data-stat={`package-pay-btn-${name}`}
              disabled={isActivationContinueDisabled}
              onClick={openPayment}
            >
              {t('common.continue')}
            </S.ContinueButton>
          </S.ActionButtonSection>
        </S.Content>
      </S.Sheet>

      <InvoicePaymentModal
        title={t('pages.cabinet.package_name', { name })}
        controller={invoicePayment}
        cashBalance={cashBalance}
        details={{
          activationDate,
          benefitPercent,
          originalPrice,
          packageName: name,
          periodDays,
          price,
          promoPrice,
          promotionDiscountAmount,
          promotionDiscountPercent,
          showPromotionLabel,
          showPromoPriceLabel,
        }}
        isLoading={isProcessingPayment}
        isOpen={isPaymentOpen}
        onBack={closePayment}
        onClose={onClose}
        onPay={handlePay}
      />

      <WarningModal
        dataTestId="invoice-precreate-warning"
        isOpen={isNullifyWarningOpen}
        onClose={closeNullifyWarning}
        title={invoiceWarning?.title ?? t('common.attention')}
        description={
          invoiceWarning?.text ?? t('pages.cabinet.package_nullify_warning')
        }
        actions={
          <>
            <Button
              appearance="secondaryOutlined"
              borderRadius={14}
              disabled={isProcessingPayment}
              isFullWidth
              onClick={closeNullifyWarning}
            >
              {t('common.decline')}
            </Button>
            <Button
              appearance="secondary"
              borderRadius={14}
              data-testid="invoice-precreate-continue"
              disabled={isProcessingPayment}
              isFullWidth
              isLoading={isProcessingPayment}
              onClick={createInvoiceAndPayment}
            >
              {t('common.continue')}
            </Button>
          </>
        }
      />
    </>
  );
}
